import React from "react";

import pdf from "@react-pdf/renderer";

import { colors } from "@/theme/overrides/colors";
import Beaker from "@assets/icons/beaker.png";
import Bowl from "@assets/icons/bowl.png";
import Cup from "@assets/icons/cup.png";
import Drop from "@assets/icons/drop.png";
import PdfHeader from "@assets/icons/pdf-header.png";
import Pitcher1 from "@assets/icons/pitcher-1.png";
import Pitcher2 from "@assets/icons/pitcher.png";
import SaltGlass from "@assets/icons/salt-glass.png";
import ScaleFruit from "@assets/icons/scale-fruit.png";
import Scale from "@assets/icons/scale.png";
import Spoon from "@assets/icons/spoon.png";
import TeaSpoon from "@assets/icons/teaspoon.png";
import Temperature from "@assets/icons/temperature.png";
import WaterLevel from "@assets/icons/water-level.png";

import Footer from "./components/Footer";
import Flex from "./components/layout/Flex";
import Subheader from "./components/Subheader";
import {
  mulish,
  mulishBold,
  mulishExtraBold,
  mulishExtraLight,
  mulishLight,
  mulishMedium,
  mulishSemiBold,
} from "./fonts";

const { Page, StyleSheet, Font, View, Text, Image } = pdf;

Font.register({
  family: "Mulish",
  fonts: [
    {
      src: mulish,
      fontWeight: "normal",
    },
    {
      src: mulishBold,
      fontWeight: "bold",
    },
    {
      src: mulishExtraBold,
      fontWeight: "ultrabold",
    },
    {
      src: mulishExtraLight,
      fontWeight: "ultralight",
    },
    {
      src: mulishLight,
      fontWeight: "light",
    },
    {
      src: mulishMedium,
      fontWeight: "medium",
    },
    {
      src: mulishSemiBold,
      fontWeight: "semibold",
    },
  ],
});

// Create styles
const styles = StyleSheet.create({
  page: {
    padding: "0px",
    fontFamily: "Mulish",
    color: colors.dark,
    fontSize: "16px",
  },
  container: {
    padding: "12px 24px 0px 24px",
  },
  title: {
    fontWeight: "bold",
    fontSize: "14px",
  },
  row: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  rowItem: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    fontSize: "10px",
    padding: "6px 4px",
  },
  dryWeight: {
    width: "55%",
    display: "flex",
    paddingRight: "3px",
  },
  liquidVolume: {
    width: "100%",
    display: "flex",
    paddingTop: "6px",
    marginTop: "17px",
    marginBottom: "20px",
  },
  ovenTemp: {
    width: "45%",
    display: "flex",
    paddingLeft: "3px",
    marginTop: "16px",
  },
  card: {
    background: colors.white,
    border: "1px solid #000000",
    borderRadius: "12px",
    paddingTop: "25px",
  },
  content: {
    width: "100%",
    borderBottomRightRadius: "12px",
    borderBottomLeftRadius: "12px",
    overflow: "hidden",
  },
  leftPanel: {
    width: "120px",
    display: "flex",
    position: "relative",
  },
  rightPanel: {
    width: "calc(100% - 120px)",
    display: "flex",
    paddingLeft: "5px",
    borderBottomRightRadius: "12px",
    overflow: "hidden",
  },
  blackBackground: {
    backgroundColor: colors.black,
    color: colors.white,
    padding: "0px 12px",
    fontWeight: 700,
  },
  centerText: {
    textAlign: "center",
    fontWeight: 600,
  },
  semiBoldText: {
    fontWeight: 700,
  },
  iconView: {
    display: "flex",
    alignItems: "center",
  },
  iconContainer: {
    width: "40px",
    height: "40px",
    border: "2px solid #000",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dashPinch: {
    position: "relative",
    marginTop: "20px",
    marginBottom: "20px",
    width: "100%",
    backgroundColor: colors.black,
    color: colors.white,
    padding: "7px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: 700,
  },
  dashPinchIcon: {
    position: "absolute",
    left: 0,
    marginLeft: "8px",
    marginTop: "12px",
    width: "50px",
    height: "20px",
  },
  liquidVolumeContainer: {
    marginTop: "60px",
    width: "100%",
  },
  liquidVolumeCard: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#F4FFFD",
    borderRadius: "12px",
    marginBottom: "10px",
    padding: "16px 0px",
  },
  spoonVolume: {
    fontSize: "12px",
    fontWeight: 800,
  },
  waterLevel: {
    position: "absolute",
    top: 0,
    marginTop: "-30px",
    left: 0,
    marginLeft: "15px",
    backgroundColor: colors.white,
  },
  headerContainer: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "absolute",
    marginTop: "-18px",
  },
  headerImage: {
    display: "flex",
    width: "200px",
    height: "37px",
  },
  headerText: {
    width: "100%",
    position: "absolute",
    top: 0,
    textAlign: "center",
    fontSize: "14px",
    fontWeight: 800,
    marginTop: "10px",
  },
  tempIcon: {
    width: "35",
    height: "35px",
    borderRadius: "50%",
    backgroundColor: colors.black,
    alignItems: "center",
    justifyContent: "center",
    marginTop: "-59px",
    top: 0,
  },
  lightGreenBackground: {
    backgroundColor: "#F4FFFD",
  },
});

const PDFHouseholdMeasurementTemplate = () => {
  return (
    <Page size="A4" style={styles.page}>
      <Subheader title="Household Measurements" />
      <View style={styles.container}>
        <View style={[styles.row, { marginTop: "30px" }]}>
          <View style={styles.dryWeight}>
            <View style={styles.card}>
              <View style={styles.headerContainer}>
                <View style={styles.headerImage}>
                  <Image
                    src={PdfHeader}
                    style={{ width: "200px", height: "37px" }}
                  />
                </View>
                <Text style={styles.headerText}>Dry Weight</Text>
              </View>
              <View style={styles.content}>
                <View style={styles.rowItem}>
                  <View style={{ width: "100%" }}>
                    <Flex justifyContent="spaceBetween" flexDirection="row">
                      <View style={[styles.iconView, { width: "20%" }]}>
                        <View style={styles.iconContainer}>
                          <Image
                            src={Scale}
                            style={{ width: "17px", height: "20px" }}
                          />
                        </View>
                      </View>
                      <View style={[styles.iconView, { width: "20%" }]}>
                        <View style={styles.iconContainer}>
                          <Image
                            src={TeaSpoon}
                            style={{ width: "17px", height: "18px" }}
                          />
                        </View>
                      </View>
                      <View style={[styles.iconView, { width: "20%" }]}>
                        <View style={styles.iconContainer}>
                          <Image
                            src={Cup}
                            style={{ width: "20px", height: "15px" }}
                          />
                        </View>
                      </View>
                      <View style={[styles.iconView, { width: "20%" }]}>
                        <View style={styles.iconContainer}>
                          <Image
                            src={Bowl}
                            style={{ width: "22px", height: "12px" }}
                          />
                        </View>
                      </View>
                      <View style={[styles.iconView, { width: "20%" }]}>
                        <View style={styles.iconContainer}>
                          <Image
                            src={ScaleFruit}
                            style={{ width: "20px", height: "22px" }}
                          />
                        </View>
                      </View>
                    </Flex>
                  </View>
                </View>
                <View style={[styles.rowItem, styles.lightGreenBackground]}>
                  <View style={{ width: "100%" }}>
                    <Flex justifyContent="spaceBetween" flexDirection="row">
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        1/2 oz
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        1 tbsp
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        1/16 C
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        15g
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        -
                      </Text>
                    </Flex>
                  </View>
                </View>
                <View style={styles.rowItem}>
                  <View style={{ width: "100%" }}>
                    <Flex justifyContent="spaceBetween" flexDirection="row">
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        1 oz
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        2 tbsp
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        1/5 C
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        28g
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        -
                      </Text>
                    </Flex>
                  </View>
                </View>
                <View style={[styles.rowItem, styles.lightGreenBackground]}>
                  <View style={{ width: "100%" }}>
                    <Flex justifyContent="spaceBetween" flexDirection="row">
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        2 oz
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        4 tbsp
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        1/4 C
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        57g
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        -
                      </Text>
                    </Flex>
                  </View>
                </View>
                <View style={styles.rowItem}>
                  <View style={{ width: "100%" }}>
                    <Flex justifyContent="spaceBetween" flexDirection="row">
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        3 oz
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        6 tbsp
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        1/3 C
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        85g
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        -
                      </Text>
                    </Flex>
                  </View>
                </View>
                <View style={[styles.rowItem, styles.lightGreenBackground]}>
                  <View style={{ width: "100%" }}>
                    <Flex justifyContent="spaceBetween" flexDirection="row">
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        4 oz
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        8 tbsp
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        1/2 C
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        115g
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        1/4 lb
                      </Text>
                    </Flex>
                  </View>
                </View>
                <View style={styles.rowItem}>
                  <View style={{ width: "100%" }}>
                    <Flex justifyContent="spaceBetween" flexDirection="row">
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        8 oz
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        16 tbsp
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        1 C
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        227g
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        1/2 lb
                      </Text>
                    </Flex>
                  </View>
                </View>
                <View style={[styles.rowItem, styles.lightGreenBackground]}>
                  <View style={{ width: "100%" }}>
                    <Flex justifyContent="spaceBetween" flexDirection="row">
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        12 oz
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        24 tbsp
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        1 1/2 C
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        340g
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        3/4 lb
                      </Text>
                    </Flex>
                  </View>
                </View>
                <View style={styles.rowItem}>
                  <View style={{ width: "100%" }}>
                    <Flex justifyContent="spaceBetween" flexDirection="row">
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        16 oz
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        32 tbsp
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        2 C
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        445g
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        1 lb
                      </Text>
                    </Flex>
                  </View>
                </View>
              </View>
            </View>

            <View style={styles.dashPinch}>
              <Image src={SaltGlass} style={styles.dashPinchIcon} />
              <View style={[styles.centerText, { width: "100%" }]}>
                <Text>Dash = 1/16 tsp</Text>
              </View>
              <View style={[styles.centerText, { width: "100%" }]}>
                <Text>Pinch = 1/8 tsp</Text>
              </View>
            </View>
          </View>
          <View style={styles.ovenTemp}>
            <View style={styles.card}>
              <View style={styles.headerContainer}>
                <View style={styles.headerImage}>
                  <Image
                    src={PdfHeader}
                    style={{ width: "200px", height: "37px" }}
                  />
                </View>
                <View style={styles.tempIcon}>
                  <Image
                    src={Temperature}
                    style={{ width: "15px", height: "25px" }}
                  />
                </View>
                <Text style={styles.headerText}>Oven Temp. conversions</Text>
              </View>
              <View style={styles.blackBackground}>
                <View style={styles.rowItem}>
                  <View style={{ width: "100%" }}>
                    <Flex justifyContent="spaceBetween" flexDirection="row">
                      <Text style={[styles.centerText, { width: "25%" }]}>
                        Fahrenheit
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        Celsius
                      </Text>
                      <Text style={[styles.centerText, { width: "15%" }]}>
                        Gas
                      </Text>
                      <Text style={[styles.centerText, { width: "40%" }]}>
                        Description
                      </Text>
                    </Flex>
                  </View>
                </View>
              </View>
              <View style={styles.content}>
                <View style={[styles.rowItem, styles.lightGreenBackground]}>
                  <View style={{ width: "100%" }}>
                    <Flex justifyContent="spaceBetween" flexDirection="row">
                      <Text style={[styles.centerText, { width: "25%" }]}>
                        225
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        110
                      </Text>
                      <Text style={[styles.centerText, { width: "15%" }]}>
                        1/4
                      </Text>
                      <Text
                        style={[
                          styles.centerText,
                          styles.semiBoldText,
                          { width: "40%" },
                        ]}
                      >
                        Very slow
                      </Text>
                    </Flex>
                  </View>
                </View>
                <View style={styles.rowItem}>
                  <View style={{ width: "100%" }}>
                    <Flex justifyContent="spaceBetween" flexDirection="row">
                      <Text style={[styles.centerText, { width: "25%" }]}>
                        250
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        120
                      </Text>
                      <Text style={[styles.centerText, { width: "15%" }]}>
                        1/2
                      </Text>
                      <Text style={[styles.centerText, { width: "40%" }]}>
                        -
                      </Text>
                    </Flex>
                  </View>
                </View>
                <View style={[styles.rowItem, styles.lightGreenBackground]}>
                  <View style={{ width: "100%" }}>
                    <Flex justifyContent="spaceBetween" flexDirection="row">
                      <Text style={[styles.centerText, { width: "25%" }]}>
                        275
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        135
                      </Text>
                      <Text style={[styles.centerText, { width: "15%" }]}>
                        1
                      </Text>
                      <Text
                        style={[
                          styles.centerText,
                          styles.semiBoldText,
                          { width: "40%" },
                        ]}
                      >
                        Slow
                      </Text>
                    </Flex>
                  </View>
                </View>
                <View style={styles.rowItem}>
                  <View style={{ width: "100%" }}>
                    <Flex justifyContent="spaceBetween" flexDirection="row">
                      <Text style={[styles.centerText, { width: "25%" }]}>
                        300
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        150
                      </Text>
                      <Text style={[styles.centerText, { width: "15%" }]}>
                        2
                      </Text>
                      <Text
                        style={[
                          styles.centerText,
                          styles.semiBoldText,
                          { width: "40%" },
                        ]}
                      >
                        -
                      </Text>
                    </Flex>
                  </View>
                </View>
                <View style={[styles.rowItem, styles.lightGreenBackground]}>
                  <View style={{ width: "100%" }}>
                    <Flex justifyContent="spaceBetween" flexDirection="row">
                      <Text style={[styles.centerText, { width: "25%" }]}>
                        325
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        165
                      </Text>
                      <Text style={[styles.centerText, { width: "15%" }]}>
                        3
                      </Text>
                      <Text
                        style={[
                          styles.centerText,
                          styles.semiBoldText,
                          { width: "40%" },
                        ]}
                      >
                        Moderately slow
                      </Text>
                    </Flex>
                  </View>
                </View>
                <View style={styles.rowItem}>
                  <View style={{ width: "100%" }}>
                    <Flex justifyContent="spaceBetween" flexDirection="row">
                      <Text style={[styles.centerText, { width: "25%" }]}>
                        350
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        180
                      </Text>
                      <Text style={[styles.centerText, { width: "15%" }]}>
                        4
                      </Text>
                      <Text
                        style={[
                          styles.centerText,
                          styles.semiBoldText,
                          { width: "40%" },
                        ]}
                      >
                        Moderate
                      </Text>
                    </Flex>
                  </View>
                </View>
                <View style={[styles.rowItem, styles.lightGreenBackground]}>
                  <View style={{ width: "100%" }}>
                    <Flex justifyContent="spaceBetween" flexDirection="row">
                      <Text style={[styles.centerText, { width: "25%" }]}>
                        375
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        190
                      </Text>
                      <Text style={[styles.centerText, { width: "15%" }]}>
                        5
                      </Text>
                      <Text
                        style={[
                          styles.centerText,
                          styles.semiBoldText,
                          { width: "40%" },
                        ]}
                      >
                        -
                      </Text>
                    </Flex>
                  </View>
                </View>
                <View style={styles.rowItem}>
                  <View style={{ width: "100%" }}>
                    <Flex justifyContent="spaceBetween" flexDirection="row">
                      <Text style={[styles.centerText, { width: "25%" }]}>
                        400
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        205
                      </Text>
                      <Text style={[styles.centerText, { width: "15%" }]}>
                        6
                      </Text>
                      <Text
                        style={[
                          styles.centerText,
                          styles.semiBoldText,
                          { width: "40%" },
                        ]}
                      >
                        Moderately hot
                      </Text>
                    </Flex>
                  </View>
                </View>
                <View style={[styles.rowItem, styles.lightGreenBackground]}>
                  <View style={{ width: "100%" }}>
                    <Flex justifyContent="spaceBetween" flexDirection="row">
                      <Text style={[styles.centerText, { width: "25%" }]}>
                        425
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        220
                      </Text>
                      <Text style={[styles.centerText, { width: "15%" }]}>
                        7
                      </Text>
                      <Text
                        style={[
                          styles.centerText,
                          styles.semiBoldText,
                          { width: "40%" },
                        ]}
                      >
                        Hot
                      </Text>
                    </Flex>
                  </View>
                </View>
                <View style={styles.rowItem}>
                  <View style={{ width: "100%" }}>
                    <Flex justifyContent="spaceBetween" flexDirection="row">
                      <Text style={[styles.centerText, { width: "25%" }]}>
                        450
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        230
                      </Text>
                      <Text style={[styles.centerText, { width: "15%" }]}>
                        8
                      </Text>
                      <Text
                        style={[
                          styles.centerText,
                          styles.semiBoldText,
                          { width: "40%" },
                        ]}
                      >
                        -
                      </Text>
                    </Flex>
                  </View>
                </View>
                <View style={[styles.rowItem, styles.lightGreenBackground]}>
                  <View style={{ width: "100%" }}>
                    <Flex justifyContent="spaceBetween" flexDirection="row">
                      <Text style={[styles.centerText, { width: "25%" }]}>
                        475
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        245
                      </Text>
                      <Text style={[styles.centerText, { width: "15%" }]}>
                        9
                      </Text>
                      <Text
                        style={[
                          styles.centerText,
                          styles.semiBoldText,
                          { width: "40%" },
                        ]}
                      >
                        Very hot
                      </Text>
                    </Flex>
                  </View>
                </View>
                <View style={styles.rowItem}>
                  <View style={{ width: "100%" }}>
                    <Flex justifyContent="spaceBetween" flexDirection="row">
                      <Text style={[styles.centerText, { width: "25%" }]}>
                        500
                      </Text>
                      <Text style={[styles.centerText, { width: "20%" }]}>
                        260
                      </Text>
                      <Text style={[styles.centerText, { width: "15%" }]}>
                        10
                      </Text>
                      <Text
                        style={[
                          styles.centerText,
                          styles.semiBoldText,
                          { width: "40%" },
                        ]}
                      >
                        Extremely hot
                      </Text>
                    </Flex>
                  </View>
                </View>
              </View>
            </View>
          </View>
        </View>
        <View style={styles.row}>
          <View style={styles.liquidVolume}>
            <View style={styles.card}>
              <View style={styles.headerContainer}>
                <View style={styles.headerImage}>
                  <Image
                    src={PdfHeader}
                    style={{ width: "200px", height: "37px" }}
                  />
                </View>
                <Text style={styles.headerText}>Liquid Volumes</Text>
              </View>
              <View style={styles.waterLevel}>
                <Image
                  src={WaterLevel}
                  style={{ width: "80px", height: "93px" }}
                />
              </View>
              <View style={styles.content}>
                <View style={styles.row}>
                  <View style={styles.leftPanel}>
                    <View style={styles.liquidVolumeContainer}>
                      <View style={styles.liquidVolumeCard}>
                        <View
                          style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Image
                            src={Spoon}
                            style={{ width: "26px", height: "8px" }}
                          />
                          <Text
                            style={[
                              styles.centerText,
                              styles.spoonVolume,
                              { marginTop: "17px" },
                            ]}
                          >
                            1 tsp = 5 ml
                          </Text>
                        </View>
                      </View>
                      <View style={styles.liquidVolumeCard}>
                        <View
                          style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Image
                            src={Spoon}
                            style={{ width: "60px", height: "15px" }}
                          />
                          <Text
                            style={[
                              styles.centerText,
                              styles.spoonVolume,
                              { marginTop: "12px" },
                            ]}
                          >
                            1 tbsp = 15 ml
                          </Text>
                        </View>
                      </View>
                    </View>
                  </View>
                  <View style={styles.rightPanel}>
                    <View style={styles.rowItem}>
                      <View style={{ width: "100%" }}>
                        <Flex justifyContent="spaceBetween" flexDirection="row">
                          <View style={[styles.iconView, { width: "14.29%" }]}>
                            <View style={styles.iconContainer}>
                              <Image
                                src={Drop}
                                style={{ width: "14px", height: "20px" }}
                              />
                            </View>
                          </View>
                          <View style={[styles.iconView, { width: "14.29%" }]}>
                            <View style={styles.iconContainer}>
                              <Image
                                src={TeaSpoon}
                                style={{ width: "17px", height: "18px" }}
                              />
                            </View>
                          </View>
                          <View style={[styles.iconView, { width: "14.29%" }]}>
                            <View style={styles.iconContainer}>
                              <Image
                                src={TeaSpoon}
                                style={{ width: "17px", height: "18px" }}
                              />
                            </View>
                          </View>
                          <View style={[styles.iconView, { width: "14.29%" }]}>
                            <View style={styles.iconContainer}>
                              <Image
                                src={Pitcher1}
                                style={{ width: "20px", height: "18px" }}
                              />
                            </View>
                          </View>
                          <View style={[styles.iconView, { width: "14.29%" }]}>
                            <View style={styles.iconContainer}>
                              <Image
                                src={Cup}
                                style={{ width: "20px", height: "16px" }}
                              />
                            </View>
                          </View>
                          <View style={[styles.iconView, { width: "14.29%" }]}>
                            <View style={styles.iconContainer}>
                              <Image
                                src={Pitcher2}
                                style={{ width: "21px", height: "21px" }}
                              />
                            </View>
                          </View>
                          <View style={[styles.iconView, { width: "14.29%" }]}>
                            <View style={styles.iconContainer}>
                              <Image
                                src={Beaker}
                                style={{ width: "22px", height: "25px" }}
                              />
                            </View>
                          </View>
                        </Flex>
                      </View>
                    </View>
                    <View style={[styles.rowItem, styles.lightGreenBackground]}>
                      <View style={{ width: "100%" }}>
                        <Flex justifyContent="spaceBetween" flexDirection="row">
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            1 oz
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            6 tsp
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            2 tbsp
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            30 ml
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            1/8 C
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            -
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            -
                          </Text>
                        </Flex>
                      </View>
                    </View>
                    <View style={styles.rowItem}>
                      <View style={{ width: "100%" }}>
                        <Flex justifyContent="spaceBetween" flexDirection="row">
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            2 oz
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            12 tsp
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            4 tbsp
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            60 ml
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            1/4 C
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            -
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            -
                          </Text>
                        </Flex>
                      </View>
                    </View>
                    <View style={[styles.rowItem, styles.lightGreenBackground]}>
                      <View style={{ width: "100%" }}>
                        <Flex justifyContent="spaceBetween" flexDirection="row">
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            2 2/3oz
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            16 tsp
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            5 tbsp
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            80 ml
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            1/3 C
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            -
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            -
                          </Text>
                        </Flex>
                      </View>
                    </View>
                    <View style={styles.rowItem}>
                      <View style={{ width: "100%" }}>
                        <Flex justifyContent="spaceBetween" flexDirection="row">
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            4 oz
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            24 tsp
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            8 tbsp
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            120 ml
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            1/2 C
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            -
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            -
                          </Text>
                        </Flex>
                      </View>
                    </View>
                    <View style={[styles.rowItem, styles.lightGreenBackground]}>
                      <View style={{ width: "100%" }}>
                        <Flex justifyContent="spaceBetween" flexDirection="row">
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            5 1/3oz
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            32 tsp
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            11 tbsp
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            160 ml
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            2/3 C
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            -
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            -
                          </Text>
                        </Flex>
                      </View>
                    </View>
                    <View style={styles.rowItem}>
                      <View style={{ width: "100%" }}>
                        <Flex justifyContent="spaceBetween" flexDirection="row">
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            6 oz
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            36 tsp
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            12 tbsp
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            177 ml
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            3/4 C
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            -
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            -
                          </Text>
                        </Flex>
                      </View>
                    </View>
                    <View style={[styles.rowItem, styles.lightGreenBackground]}>
                      <View style={{ width: "100%" }}>
                        <Flex justifyContent="spaceBetween" flexDirection="row">
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            8 oz
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            48 tsp
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            16 tbsp
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            240 ml
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            1 C
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            1/2 pt
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            1/4 qt
                          </Text>
                        </Flex>
                      </View>
                    </View>
                    <View style={styles.rowItem}>
                      <View style={{ width: "100%" }}>
                        <Flex justifyContent="spaceBetween" flexDirection="row">
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            16 oz
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            96 tsp
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            32 tbsp
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            470 ml
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            2 C
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            1 pt
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            1/2 qt
                          </Text>
                        </Flex>
                      </View>
                    </View>
                    <View style={[styles.rowItem, styles.lightGreenBackground]}>
                      <View style={{ width: "100%" }}>
                        <Flex justifyContent="spaceBetween" flexDirection="row">
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            32 oz
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            192 tsp
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            64 tbsp
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            950 ml
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            4 C
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            2 pt
                          </Text>
                          <Text
                            style={[styles.centerText, { width: "14.29%" }]}
                          >
                            1 qt
                          </Text>
                        </Flex>
                      </View>
                    </View>
                  </View>
                </View>
              </View>
            </View>
          </View>
        </View>
        <Footer />
      </View>
    </Page>
  );
};

export default PDFHouseholdMeasurementTemplate;
